import {batchActions} from 'redux-batch-enhancer'
import {CALL_API, Call} from 'redux/middleware/api'
import {reportError} from 'redux/modules/error/actions'
import {showToast} from 'redux/modules/ui/toaster/actions'
import {
  getOrderedTimePeriodRanges,
  getTimePeriodRanges,
  getAggregatedSites,
  getEndTotalDate,
  getSelectedDateRange
} from 'redux/modules/currentUser/selectors'
import {
  getIncommingTrafficData
} from './selectors'
import formatDateWithTz from 'utils/formatDateWithTz'
import json2csv from 'json2csv'
import * as _ from 'lodash'

import {
  INSIGHT_TRAFFIC,
  INSIGHT_TOTAL_CURRENT_TRAFFIC,
  INSIGHT_TOTAL_PREVIOUS_TRAFFIC,
  INSIGHT_TRAFFIC_SORTING
} from './traffic'

import {
  INSIGHT_LANDINGS_TOTAL_TRAFFIC,
  INSIGHT_LANDINGS_WORST,
  INSIGHT_LANDINGS_BEST,
  INSIGHT_LANDINGS_VALUABLE,
  INSIGHT_LANDINGS_FREQUENT,
  INSIGHT_FILTER
} from './landings'

import {
  FUNNEL_CONVERSION,
  FUNNEL_CONVERSION_FOR_CHANNEL,
  FUNNEL_CONVERSION_DATA_CLEAR,
  FUNNEL_TOTAL_TRAFFIC,
  FUNNEL_TRAFFIC_BY_CHANNELS,
  FUNNEL_CHANGE_SELECTED_CHANNELS
} from './funnels'

import {
  INSIGHT_ORDERS,
  INSIGHT_TOTAL_CURRENT_ORDERS,
  INSIGHT_TOTAL_PREVIOUS_ORDERS,
  INSIGHT_ORDERS_METRIC,
  INSIGHT_ORDERS_COST_METRIC,
  INSIGHT_ORDERS_SORTING
} from './orders'

import {
  INCOMMING_TRAFFIC,
  INCOMMING_TOTAL_TRAFFIC,
  SESSION_MAPPING_DATA
} from './incomming'
const statsVersion = localStorage.getItem('statsVersion') === 'v1' ? '' : 'v2/'

function loadOrders({merchantSiteId, startDate, endDate, metrics, targetCurrency, timezone, conversionType}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_ORDERS,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions: ['channelGroup', 'channel'],
        metrics,
        targetCurrency,
        timeUnit: 'day',
        timezone,
        conversionType
      }
    }
  }
}

function loadTotal(
  {merchantSiteId, startDate, endDate, metrics, targetCurrency, timezone, dimensions, filter, conversionType},
  types
) {
  dimensions = dimensions || ['channelGroup', 'channel']
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions,
        metrics,
        targetCurrency,
        timeUnit: 'total',
        filter,
        timezone,
        conversionType
      }
    }
  }
}

function loadTraffic(
  {merchantSiteId, startDate, endDate, metrics, targetCurrency, timezone, dimensions, filter, timeUnit},
  types = INSIGHT_TRAFFIC
) {
  dimensions = dimensions || ['channelGroup', 'channel']
  timeUnit = timeUnit || 'day'
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions,
        metrics,
        targetCurrency,
        timeUnit,
        filter,
        timezone
      }
    }
  }
}

function getQueryParamsForLandings(sortKey, sortDirection, params) {
  const {merchantSiteId, startDate, endDate, channelGroupId, channelId, trafficMin = 0, timezone} = params

  return {
    merchantSiteId,
    range: {
      start: startDate,
      end: endDate
    },
    dimensions: ['landingPage'],
    metrics: [
      'session:count',
      'order:total',
      'session:valuePerVisit',
      'session:bounceRate',
      'session:conversionRate'
    ],
    metricsFilter: {
      'session:count': {'gt': trafficMin}
    },
    filter: {
      channel: channelId,
      channelGroup: channelGroupId
    },
    [sortKey]: sortDirection,
    limit: 10,
    timezone
  }
}

function loadTotalTrafficCount({merchantSiteId, startDate, endDate, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_LANDINGS_TOTAL_TRAFFIC,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        metrics: ['session:count', 'cost:total'],
        timeUnit: 'total',
        timezone
      }
    }
  }
}

function loadInsightLandingValuable(params) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_LANDINGS_VALUABLE,
      queryParams: getQueryParamsForLandings('sort[order:total]', 'desc', params)
    }
  }
}

function loadInsightLandingFrequent(params) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_LANDINGS_FREQUENT,
      queryParams: getQueryParamsForLandings('sort[session:count]', 'desc', params)
    }
  }
}

function loadInsightLandingBest(params) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_LANDINGS_BEST,
      queryParams: getQueryParamsForLandings('sort[session:valuePerVisit]', 'desc', params)
    }
  }
}

function loadInsightLandingWorst(params) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: INSIGHT_LANDINGS_WORST,
      queryParams: getQueryParamsForLandings('sort[session:valuePerVisit]', 'asc', params)
    }
  }
}

function loadConversion({merchantSiteId, startDate, endDate, channelGroup, channel, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: FUNNEL_CONVERSION,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions: ['funnelStatus'],
        metrics: ['session:count'],
        timezone
      }
    }
  }
}

function loadChannelConversionForChannel({merchantSiteId, startDate, endDate, channelGroup, channel, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: FUNNEL_CONVERSION_FOR_CHANNEL,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions: ['funnelStatus'],
        filter: {
          channelGroup,
          channel
        },
        metrics: ['session:count'],
        timezone
      }
    }
  }
}

function loadTotalFunnelTraffic({merchantSiteId, startDate, endDate, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: FUNNEL_TOTAL_TRAFFIC,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        metrics: ['session:count'],
        timezone
      }
    }
  }
}

function loadTrafficByChannels({merchantSiteId, startDate, endDate, channel, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: FUNNEL_TRAFFIC_BY_CHANNELS,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        dimensions: ['channelGroup', 'channel'],
        metrics: ['session:count'],
        timezone
      }
    }
  }
}

function loadSessionMappingData({merchantSiteId, startDate, endDate, channel, timezone}) {
  return {
    [CALL_API]: {
      endpoint: `sites/${merchantSiteId}/sessionMappingData`,
      types: SESSION_MAPPING_DATA,
      queryParams: {
        merchantSiteId,
        range: {
          start: startDate,
          end: endDate
        },
        timezone
      }
    }
  }
}

export function fetchCostsViewOrders(metric = '', crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, crossSiteMode)
    const {start, end} = getTimePeriodRanges(state)
    const {start: orderedStart, end: orderedEnd} = getOrderedTimePeriodRanges(state)
    const metrics = [metric]
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(orderedEnd.start, timezone),
      endDate: formatDateWithTz(orderedStart.end, timezone),
      targetCurrency,
      metrics,
      timezone
    }

    dispatch(() => {
      return Call.all([
        dispatch(loadOrders(fetchParams)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(start.start, timezone),
          endDate: formatDateWithTz(start.end, timezone)
        }, INSIGHT_TOTAL_CURRENT_ORDERS)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(end.start, timezone),
          endDate: formatDateWithTz(getEndTotalDate(start, end.end, state), timezone)
        }, INSIGHT_TOTAL_PREVIOUS_ORDERS))
      ])
    })
      .fail(dispatch(reportError()))
  }
}

export function fetchOrders(metric = '', conversionType, crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, crossSiteMode)
    const {start, end} = getTimePeriodRanges(state)
    const {start: orderedStart, end: orderedEnd} = getOrderedTimePeriodRanges(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(orderedEnd.start, timezone),
      endDate: formatDateWithTz(orderedStart.end, timezone),
      metrics: [metric],
      targetCurrency,
      timezone,
      conversionType
    }

    dispatch(() => {
      return Call.all([
        dispatch(loadOrders(fetchParams)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(start.start, timezone),
          endDate: formatDateWithTz(start.end, timezone)
        }, INSIGHT_TOTAL_CURRENT_ORDERS)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(end.start, timezone),
          endDate: formatDateWithTz(getEndTotalDate(start, end.end, state), timezone)
        }, INSIGHT_TOTAL_PREVIOUS_ORDERS))
      ])
    })
      .fail(dispatch(reportError()))
  }
}

export function fetchCostsViewTraffic(metrics = [], crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, crossSiteMode)
    const {start, end} = getTimePeriodRanges(state)
    const {start: orderedStart, end: orderedEnd} = getOrderedTimePeriodRanges(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(orderedEnd.start, timezone),
      endDate: formatDateWithTz(orderedStart.end, timezone),
      metrics,
      targetCurrency,
      timezone
    }

    dispatch(() => {
      return Call.all([
        dispatch(loadTraffic(fetchParams)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(start.start, timezone),
          endDate: formatDateWithTz(start.end, timezone)
        }, INSIGHT_TOTAL_CURRENT_TRAFFIC)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(end.start, timezone),
          endDate: formatDateWithTz(getEndTotalDate(start, end.end, state), timezone)
        }, INSIGHT_TOTAL_PREVIOUS_TRAFFIC))
      ])
    })
      .fail(dispatch(reportError()))
  }
}

export function fetchInsightTraffic(metrics = [], crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, crossSiteMode)
    const {start, end} = getTimePeriodRanges(state)
    const {start: orderedStart, end: orderedEnd} = getOrderedTimePeriodRanges(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(orderedEnd.start, timezone),
      endDate: formatDateWithTz(orderedStart.end, timezone),
      targetCurrency,
      metrics,
      timezone
    }

    dispatch(() => {
      return Call.all([
        dispatch(loadTraffic(fetchParams)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(start.start, timezone),
          endDate: formatDateWithTz(start.end, timezone)
        }, INSIGHT_TOTAL_CURRENT_TRAFFIC)),
        dispatch(loadTotal({
          ...fetchParams,
          startDate: formatDateWithTz(end.start, timezone),
          endDate: formatDateWithTz(getEndTotalDate(start, end.end, state), timezone)
        }, INSIGHT_TOTAL_PREVIOUS_TRAFFIC))
      ])
    })
      .fail(dispatch(reportError()))
  }
}

export function fetchIncommingTraffic({
  metrics = [],
  selectedChannels,
  channelTypes,
  timeUnit,
  timezone,
  fetchTotal = true
}) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, false)
    const {startDate, endDate} = getSelectedDateRange(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      dimensions: [],
      metrics,
      timeUnit,
      timezone
    }

    if (selectedChannels?.length) {
      fetchParams.filter = {
        channelType: channelTypes[0],
        channelGroup: selectedChannels[0].channelGroup,
        channel: selectedChannels[0].channel
      }
    }

    if (channelTypes?.length) {
      fetchParams.filter = {
        ...fetchParams.filter,
        channelType: channelTypes[0]
      }
    }

    const actions = [
      dispatch(loadTraffic(fetchParams, INCOMMING_TRAFFIC))
    ]

    if (fetchTotal) {
      actions.push(
        dispatch(loadTotal({...fetchParams}, INCOMMING_TOTAL_TRAFFIC))
      )
    }

    dispatch(() => {
      return Call.all(actions)
    }).fail(dispatch(reportError()))
  }
}

export function fetchSessionMappingData(timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state, false)
    const {startDate, endDate} = getSelectedDateRange(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      timezone
    }

    return dispatch(loadSessionMappingData(fetchParams, INCOMMING_TRAFFIC))
  }
}

export function changeSort({sortKey, sortDirection}) {
  return {
    type: INSIGHT_TRAFFIC_SORTING,
    sortKey,
    sortDirection
  }
}

export function changeOrdersSort({sortKey, sortDirection}) {
  return {
    type: INSIGHT_ORDERS_SORTING,
    sortKey,
    sortDirection
  }
}

export function changeSelectedMetric(selectedMetric) {
  return {
    type: INSIGHT_ORDERS_METRIC,
    selectedMetric
  }
}

export function changeSelectedCostMetric(selectedMetric) {
  return {
    type: INSIGHT_ORDERS_COST_METRIC,
    selectedMetric
  }
}

export function setChannelFilter({channelGroupId, channelId}) {
  return {
    type: INSIGHT_FILTER,
    channelId,
    channelGroupId
  }
}

export function clearConversionData() {
  return {
    type: FUNNEL_CONVERSION_DATA_CLEAR
  }
}

export function fetchInsightLandings(startDate, endDate, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state)
    let fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      timezone
    }

    dispatch(loadTotalTrafficCount(fetchParams))
      .success(() => {
        const {
          totalTraffic,
          selectedChannel: {
            channelGroupId,
            channelId
          }
        } = getState().insights.landings

        fetchParams = {
          ...fetchParams,
          channelGroupId,
          channelId,
          trafficMin: Math.round(totalTraffic * 0.001)
        }

        Call.all(dispatch(batchActions([
          loadInsightLandingValuable(fetchParams),
          loadInsightLandingFrequent(fetchParams),
          loadInsightLandingBest(fetchParams),
          loadInsightLandingWorst(fetchParams)
        ]))).fail(dispatch(reportError()))
      })
      .fail(dispatch(reportError()))
  }
}

export function fetchAllFunnelData(startDate, endDate, selectedChannels, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state)
    const fetchParams = {
      merchantSiteId: sites,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      timezone
    }
    const conversionParams = selectedChannels.filter(Boolean).map(item => {
      const {channelGroup, channel} = item
      return {
        ...fetchParams,
        channelGroup,
        channel
      }
    })

    dispatch(clearConversionData())
    Call.all(
      dispatch(batchActions([
        loadTotalFunnelTraffic(fetchParams),
        loadTrafficByChannels(fetchParams),
        loadConversion(fetchParams),
        ...conversionParams.map(fetchParams => loadChannelConversionForChannel(fetchParams))
      ]))
    ).fail(dispatch(reportError()))
  }
}

export function fetchChannelFunnelData(startDate, endDate, selectedChannels, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const sites = getAggregatedSites(state)
    selectedChannels.forEach(item => {
      if (!item) return null
      const {channelGroupId: channelGroup, channelId: channel} = item
      const fetchParams = {
        merchantSiteId: sites,
        startDate: formatDateWithTz(startDate, timezone),
        endDate: formatDateWithTz(endDate, timezone),
        channelGroup,
        channel,
        timezone
      }
      dispatch(loadChannelConversionForChannel(fetchParams))
        .fail(dispatch(reportError()))
    })
  }
}

export function changeSelectedChannel(index, value) {
  return {
    type: FUNNEL_CHANGE_SELECTED_CHANNELS,
    index,
    value
  }
}

export function removeSelectedChannel(index) {
  return {
    type: FUNNEL_CHANGE_SELECTED_CHANNELS,
    index
  }
}

export function exportIncommingData() {
  return (dispatch, getState) => {
    const metrics = getIncommingTrafficData(getState())
    const results = {}
    _.map(metrics, metricData => {
      const {data, metric, total} = metricData
      _.map(data, ({time, value, valueSum}) => {
        _.set(results, [time, metric], value)
        _.set(results, [time, 'time'], time)
      })

      _.set(results, ['total', 'time'], 'Total')
      _.set(results, ['total', metric], total)
    })

    console.log('export')

    const data = _.sortBy(_.values(results), 'time')

    const csvHeader = 'data:text/csv;charset=utf-8,'
    const csvFile = encodeURI(
      csvHeader +
        json2csv({
          data,
          defaultValue: 0,
          fields: ['time', 'order:regCount-non-direct-last-click', 'order:ftdCount-non-direct-last-click', 'session:count', 'impression:count'],
          fieldNames: ['Time', 'Reg Finished Conversions', 'FTD Conversions', 'Sessions', 'Impressions']
        })
    )
    const link = document.createElement('a')
    link.setAttribute('href', csvFile)
    const timestamp = new Date().getTime()
    link.setAttribute('download', `imcomming data export.${timestamp}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export function exportSessionMappingData(timezone) {
  return (dispatch, getState) => {
    return dispatch(fetchSessionMappingData(timezone))
    .success(data => {
      console.log(data.length)
      const csvHeader = 'data:text/csv;charset=utf-8,'
      const csvData = json2csv({
        data,
        fields: ['firstSessionTime', 'url', 'referrer', 'channel', 'channelGroup', 'numberSession'],
        defaultValue: 0
      })

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a')
      link.setAttribute('href', url)
      const timestamp = new Date().getTime()
      link.setAttribute('download', `session mapping data.${timestamp}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      if (data.length >= 500000) {
        dispatch(showToast(
          "Only first 500k rows exported. To get the full data, select shorter timeframe"
          , {type: 'warning'})
        )
      }
    })
    .fail(dispatch(reportError()))  
  }
}



// WEBPACK FOOTER //
// ./client/redux/modules/insights/actions.js